import React, { useState } from 'react'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { money } from '../financial/formatting'
import { monthLabel, errorDetail } from './overview'

// ─────────────────────────────────────────────────────────────────────────────
//   Registrar un mes de un gasto fijo
//
//   El importe viene precargado con la tarifa vigente y es editable, porque la
//   factura de este mes puede no ser la de siempre. Cuando difiere, el backend
//   NO elige por nosotros: contesta 422 con los dos caminos ya estructurados en
//   `options[]`, y esta pantalla pinta un botón por cada uno.
//
//   Las etiquetas y las consecuencias salen literales de `option.label` y
//   `option.effect`. No se re-derivan ni se escriben a mano, y el motivo está
//   en el propio esquema del backend: si cada cliente reconstruyera los dos
//   caminos a partir del texto del error, tarde o temprano uno ofrecería sólo
//   el cómodo. Son futuros distintos y fallan en direcciones opuestas —
//   "solo este mes" esconde una subida permanente tras un pronóstico que se
//   queda corto cada mes, y "de aquí en adelante" reescribe la tarifa esperada
//   por una factura rara.
//
//   Por eso tampoco hay un botón primario entre los dos: destacar uno sería un
//   default silencioso, que es exactamente lo que el 422 existe para no dar.
// ─────────────────────────────────────────────────────────────────────────────

export default function RegisterRecurringModal({ recurring, month, onClose, onRegistered }) {
  const [amount, setAmount] = useState(String(recurring.amount))
  const [saving, setSaving] = useState(false)
  // El 422 que trae los dos caminos. Se distingue por la PRESENCIA de
  // `options`, nunca leyendo el mensaje: el mismo 422 lo devuelven también
  // InvalidPeriod, RecurringPeriodOutOfWindow, InvalidAmount y CatalogRowInactive,
  // y ésos vienen como un `{detail}` a secas.
  const [scopeRequired, setScopeRequired] = useState(null)
  const [inlineError, setInlineError] = useState(null)
  // 409: el mes ya está reclamado, o la tarifa se cerró mientras esta pestaña
  // la tenía cargada. No es un formulario que arreglar — es un estado que ya
  // no admite este envío.
  const [conflict, setConflict] = useState(null)

  const submit = async (scope = null) => {
    const amt = Number(amount)
    if (!amt || amt <= 0) { toast.error('Enter a valid amount greater than 0'); return }

    setSaving(true)
    setInlineError(null)
    try {
      const res = await api.post(`/corporate/recurring-expenses/${recurring.id}/payments`, {
        period: month,
        amount: amt,
        ...(scope ? { scope } : {}),
      })
      // `superseded` sólo viene cuando la tarifa cambió de verdad. Merece
      // decirse: el usuario acaba de cerrar una tarifa y abrir otra, que es
      // bastante más que registrar un pago.
      if (res.data?.superseded) {
        toast.success('Payment recorded, and the standing rate was replaced from this month on')
      } else {
        toast.success('Payment recorded')
      }
      onRegistered()
    } catch (err) {
      const status = err?.response?.status
      const data = err?.response?.data

      if (status === 422 && Array.isArray(data?.options) && data.options.length > 0) {
        setScopeRequired(data)
      } else if (status === 409) {
        setConflict(errorDetail(err, 'This month is already registered for this charge.'))
      } else {
        setInlineError(errorDetail(err, 'Could not register the payment'))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card cf-modal" style={{ maxWidth: 560, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0 }}>Register {monthLabel(month)}</h3>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
          {recurring.vendor_name} · {recurring.category_name}
        </p>

        {conflict ? (
          // El `detail` del backend, literal. Dice cuál es el conflicto —el mes
          // ya reclamado, o reclamado por otro escritor mientras esta llamada
          // volaba— y qué lo libera: anular la entrada, que conserva el
          // registro del error. Un "no se pudo registrar" genérico borraría
          // justo eso.
          <div style={{ padding: '4px 0 0' }}>
            <div
              style={{
                padding: '10px 12px', borderRadius: 'var(--radius)',
                background: 'var(--amber-bg)', border: '1px solid var(--amber)',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                Nothing was recorded
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                {conflict}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : scopeRequired ? (
          <div style={{ padding: '4px 0 0' }}>
            {/* El `detail` del 422, literal: nombra el importe, la tarifa y el
                proveedor. */}
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.55 }}>
              {scopeRequired.detail}
            </p>
            <div style={{ display: 'flex', gap: 18, marginTop: 12, fontSize: 12.5, color: 'var(--text-secondary)' }}>
              <span>Standing rate <strong>{money(scopeRequired.expected)}</strong></span>
              <span>This invoice <strong>{money(scopeRequired.amount)}</strong></span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              {/* Un botón por `option`, en el orden en que vienen. Ni se
                  reordenan ni se filtran ni se destaca ninguno. */}
              {scopeRequired.options.map((option) => (
                <div
                  key={option.scope}
                  style={{
                    border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                    padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
                  }}
                >
                  <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                    {option.effect}
                  </p>
                  <button
                    // Los DOS llevan btn-primary. No es un descuido: son dos
                    // elecciones reales y de igual peso, y darle a una el
                    // realce y a la otra el tratamiento secundario sería
                    // reintroducir en CSS el default que el 422 se niega a dar.
                    className="btn btn-primary btn-sm"
                    style={{ alignSelf: 'flex-start' }}
                    disabled={saving}
                    onClick={() => submit(option.scope)}
                  >
                    {option.label}
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className="form-label" htmlFor="cf-rec-amount">Amount (USD)</label>
              <input
                id="cf-rec-amount" className="input cf-input" type="number" min="0" step="0.01"
                autoFocus value={amount} onChange={(e) => setAmount(e.target.value)}
              />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                Prefilled with the rate in force, {money(recurring.amount)}. Change it if this
                month was billed differently — you will be asked what the change means.
              </div>
            </div>

            {inlineError && (
              <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--red)', lineHeight: 1.55 }}>
                {inlineError}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={() => submit()} disabled={saving}>
                {saving ? 'Recording…' : 'Record payment'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
